<script setup lang="ts">
import type { IFeedItemDataClientDTO } from '~/shared/api/client/feed/feed.dto';
import { getMaterialLink } from '~/shared/lib/getMaterialLink';

const props = defineProps<IFeedItemDataClientDTO>();

/*
  Мнение эксперта в блоке публикатора лежит в поле expert, а не news,
  поэтому запись берём из того поля, которое заполнено: иначе ссылка
  собиралась из undefined и вела на 404.
*/
const entry = computed(() => props.news ?? props.expert ?? null);
const setContentTab = inject<(tab: 'main' | 'news' | 'companies') => void>(
  'setContentTab',
  () => undefined,
);

const isRedirect = computed(() =>
  Boolean(entry.value?.redirect && entry.value.redirect.length > 0),
);
const hasCategoryImage = computed(() => entry.value?.category?.image_category);
const isPartnerCategory = computed(() =>
  Boolean(entry.value?.category?.is_partner),
);

const TELEGRAM_CHANNEL_TEXT = 'CRE в Telegram';
const TYPES_WITH_ICON = ['thunder', 'telegram'];

const link = computed(() => (entry.value ? getMaterialLink(entry.value) : ''));

const categoryLink = computed(() => `/category/${entry.value?.category?.id}`);

const handleItemClick = (event: MouseEvent) => {
  const target = event.target;

  if (
    isRedirect.value ||
    !(target instanceof Element) ||
    !target.closest('a')
  ) {
    return;
  }

  setContentTab('main');
};
</script>

<template>
  <div
    :class="[
      $style.item,
      {
        [$style[`item_${props?.type_item}`]!]:
          props?.type_item && !hasCategoryImage,
      },
    ]"
    @click.capture="handleItemClick"
  >
    <NuxtLink
      v-if="entry?.category?.name && entry?.category?.id"
      :class="[
        $style.item__category,
        {
          [$style.item__category_paddingIcon!]: props?.type_item === 'thunder',
          [$style.item__category_paddingCategory!]: hasCategoryImage,
        },
      ]"
      :to="categoryLink"
    >
      {{ entry?.category?.name }}
    </NuxtLink>

    <NuxtLink
      v-if="entry?.company_logo"
      :class="$style.item__company"
      :to="link"
      :target="isRedirect ? '_blank' : undefined"
    >
      <img
        :src="entry?.company_logo"
        :alt="props.title"
        :class="$style.item__companyImage"
      />
    </NuxtLink>

    <NuxtLink
      :class="[
        $style.item__title,
        {
          [$style.item__title_padding!]:
            props?.type_item && !(entry?.category?.name && entry?.category?.id),
        },
      ]"
      :to="link"
      :target="isRedirect ? '_blank' : undefined"
    >
      {{ props.title }}
    </NuxtLink>

    <NuxtLink
      v-if="entry?.category?.image_category"
      :class="[
        $style.item__categoryImageWrapper,
        { [$style.item__categoryImageWrapper_partner!]: isPartnerCategory },
      ]"
      :to="categoryLink"
      :target="isRedirect ? '_blank' : undefined"
    >
      <img
        :class="[
          $style.item__categoryImage,
          { [$style.item__categoryImage_partner!]: isPartnerCategory },
        ]"
        :src="entry?.category?.image_category.file_url"
        :alt="entry?.category?.name"
      />
    </NuxtLink>

    <NuxtLink
      v-else-if="props?.type_item === 'telegram'"
      :class="[$style.item__telegram]"
      :to="link"
      :target="isRedirect ? '_blank' : undefined"
    >
      {{ TELEGRAM_CHANNEL_TEXT }}
    </NuxtLink>

    <Icon
      v-else-if="TYPES_WITH_ICON.includes(props?.type_item as string)"
      :class="[
        $style.item__icon,
        { [$style[`item__icon_${props?.type_item}`]!]: props?.type_item },
      ]"
      :name="`my-icon:i-${props?.type_item}`"
      size="24"
    />
  </div>
</template>

<style lang="scss" module>
.item {
  display: flex;
  flex-flow: column nowrap;
  gap: var(--space-1x);
  border-bottom: 1px solid var(--color-gray-line);
  position: relative;

  &:last-of-type {
    border-bottom: none;
  }

  &_red {
    border-left: 4px solid var(--color-red-1);
    background: linear-gradient(90deg, #b9061833 0%, #dcbc3e00 100%);
  }

  &_thunder {
    border-left: 4px solid var(--color-yellow-1);
    background: linear-gradient(90deg, #dcbc3e33 0%, #dcbc3e00 100%);
  }

  &_telegram {
    border-left: 4px solid var(--color-telegram);
    background: linear-gradient(
      90deg,
      rgba(42, 171, 238, 0.2) 0%,
      rgba(42, 171, 238, 0) 100%
    );
  }

  &__category {
    display: block;
    @include text_caption2_caps;
    color: var(--color-gray-text);
    text-decoration: none;

    &_paddingIcon {
      padding-right: 34px;
      z-index: 1;
    }

    &_paddingCategory {
      padding-right: 64px;
      z-index: 1;
    }
  }

  &__company {
    display: flex;
    text-decoration: none;
    &Image {
      height: 20px;
    }
  }

  &__title {
    display: block;
    @include text_body2;
    color: var(--color-black-1);
    text-decoration: none;

    &_paddingIcon {
      padding-right: 34px;
      z-index: 1;
    }

    &_paddingCategory {
      padding-right: 64px;
      z-index: 1;
    }
  }

  &__telegram {
    display: block;
    @include text_caption2_caps;
    color: var(--color-telegram);
    text-decoration: none;
  }

  &__icon {
    position: absolute;
    top: 20px;
    right: 20px;

    &_thunder {
      path {
        fill: var(--color-yellow-1);
      }
    }
  }

  &__categoryImage {
    height: 15px;
    width: auto;

    &_partner {
      width: 20px;
      height: 20px;
      object-fit: contain;
    }

    &Wrapper {
      position: absolute;
      display: flex;
      top: 19px;
      right: 20px;

      &_partner {
        right: 0;
      }
    }
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}
</style>
